import { useState } from 'react';
import type { ReactElement } from 'react';
import { MIN_BACKUP_PASSWORT_LAENGE } from '@mcikm-vote/shared';
import type { BackupEintrag, WiederherstellungVorschau } from '@mcikm-vote/shared';

import {
  bereiteWiederherstellungVor,
  brichWiederherstellungAb,
  fuehreWiederherstellungAus,
  ladeSicherheitsbackup,
} from '../../admin/backupsApi';
import { ApiFehler } from '../../api/anfrage';
import { Feld } from '../../components/Feld';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { Karte } from '../../components/Karte';
import { Knopf } from '../../components/Knopf';
import { formatiereZeitpunkt } from '../../formatierung';

/**
 * Wiederherstellung eines Backups (§28.2).
 *
 * Die Oberfläche bildet die drei Schritte ab, die das Pflichtenheft verlangt — und sie lässt sich
 * nicht abkürzen: Ohne heruntergeladenes Sicherheitsbackup bleibt die Schaltfläche zum Ersetzen
 * gesperrt, und der Server weist einen Versuch ohnehin ab.
 *
 * **Die Warnung steht vor der Auswahl, nicht dahinter.** Eine Wiederherstellung ersetzt alles;
 * wer das erst nach dem Klick erfährt, erfährt es zu spät.
 */
export function WiederherstellungAbschnitt({
  backups,
  onFertig,
}: {
  backups: BackupEintrag[];
  onFertig: () => void;
}): ReactElement {
  const [datei, setDatei] = useState<File | null>(null);
  const [ausVerzeichnis, setAusVerzeichnis] = useState('');
  const [passwort, setPasswort] = useState('');
  const [sicherheitspasswort, setSicherheitspasswort] = useState('');
  const [alterSchluessel, setAlterSchluessel] = useState('');

  const [vorschau, setVorschau] = useState<WiederherstellungVorschau | null>(null);
  const [geladen, setGeladen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<string | null>(null);

  const gewaehlt = backups.find((eintrag) => eintrag.dateiname === ausVerzeichnis);
  // Ein interner Sicherungsstand ist mit INTERNAL_BACKUP_KEY verschlüsselt — ohne Passwort (§28.3).
  const brauchtPasswort = datei !== null || gewaehlt?.art === 'manuell';
  const bereit =
    (datei !== null || ausVerzeichnis !== '') &&
    sicherheitspasswort.length >= MIN_BACKUP_PASSWORT_LAENGE &&
    (!brauchtPasswort || passwort.length > 0);

  function melde(problem: unknown): void {
    setFehler(problem instanceof ApiFehler ? problem.message : 'Der Vorgang ist fehlgeschlagen.');
  }

  async function vorbereiten(): Promise<void> {
    setLaeuft(true);
    setFehler(null);
    try {
      setVorschau(
        await bereiteWiederherstellungVor({
          ...(datei === null ? { dateiname: ausVerzeichnis } : { datei }),
          ...(brauchtPasswort ? { passwort } : {}),
          sicherheitspasswort,
          ...(alterSchluessel === '' ? {} : { alterSchluessel }),
        }),
      );
      setGeladen(false);
    } catch (problem) {
      melde(problem);
    } finally {
      setLaeuft(false);
    }
  }

  async function sicherheitsbackupLaden(): Promise<void> {
    if (vorschau === null) {
      return;
    }
    setLaeuft(true);
    setFehler(null);
    try {
      await ladeSicherheitsbackup(vorschau.vorgangId, vorschau.sicherheitsbackup);
      setGeladen(true);
    } catch (problem) {
      melde(problem);
    } finally {
      setLaeuft(false);
    }
  }

  async function ausfuehren(): Promise<void> {
    if (vorschau === null) {
      return;
    }
    setLaeuft(true);
    setFehler(null);
    try {
      const antwort = await fuehreWiederherstellungAus(vorschau.vorgangId);
      setErgebnis(
        `${antwort.zeilen} Zeilen eingespielt, ${antwort.beendeteSitzungen} Sitzung(en) beendet.`,
      );
      setVorschau(null);
      onFertig();
    } catch (problem) {
      melde(problem);
    } finally {
      setLaeuft(false);
    }
  }

  async function abbrechen(): Promise<void> {
    if (vorschau !== null) {
      await brichWiederherstellungAb(vorschau.vorgangId).catch(() => undefined);
    }
    setVorschau(null);
    setGeladen(false);
  }

  return (
    <Karte>
      <h2 className="text-base font-semibold">Wiederherstellung</h2>

      <p className="border-fehler/50 bg-fehler/10 mt-3 rounded-md border p-3 text-sm">
        <strong>Eine Wiederherstellung ersetzt den gesamten Datenbestand.</strong> Veranstaltungen,
        Abstimmungen, Tokens und Ergebnisse werden durch den Stand des Backups ersetzt; alles, was
        seither entstanden ist, geht verloren. Ein Zusammenführen gibt es nicht. Vorher wird ein
        Sicherheitsbackup erzeugt, das Sie herunterladen müssen — es ist der Weg zurück.
      </p>

      {ergebnis !== null && (
        <p
          role="status"
          className="border-rand bg-flaeche-gedaempft mt-3 rounded-md border p-3 text-sm"
        >
          Die Wiederherstellung ist abgeschlossen: {ergebnis} Alle Sitzungen wurden beendet — bitte
          melden Sie sich neu an.
        </p>
      )}

      {fehler !== null && (
        <div className="mt-3">
          <Fehlermeldung ausfuehrlich>{fehler}</Fehlermeldung>
        </div>
      )}

      {vorschau === null ? (
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Backupdatei hochladen</span>
            <input
              type="file"
              accept=".mcikm"
              onChange={(ereignis) => {
                setDatei(ereignis.target.files?.[0] ?? null);
                setAusVerzeichnis('');
              }}
              className="border-rand bg-flaeche min-h-touchflaeche rounded-md border p-2"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">… oder vorhandenes Backup wählen</span>
            <select
              value={ausVerzeichnis}
              onChange={(ereignis) => {
                setAusVerzeichnis(ereignis.target.value);
                setDatei(null);
              }}
              className="border-rand bg-flaeche min-h-touchflaeche rounded-md border px-3"
            >
              <option value="">— keines —</option>
              {backups
                .filter((eintrag) => eintrag.passtZumSchema)
                .map((eintrag) => (
                  <option key={eintrag.dateiname} value={eintrag.dateiname}>
                    {formatiereZeitpunkt(eintrag.erstelltAm)} ·{' '}
                    {eintrag.art === 'manuell' ? 'manuell' : 'Sicherungsstand'} ·{' '}
                    {eintrag.dateiname}
                  </option>
                ))}
            </select>
          </label>

          {brauchtPasswort && (
            <Feld
              name="wh-passwort"
              beschriftung="Passwort des Backups"
              typ="password"
              wert={passwort}
              pflicht
              onAendern={setPasswort}
            />
          )}

          <Feld
            name="wh-sicherheitspasswort"
            beschriftung="Passwort für das Sicherheitsbackup"
            typ="password"
            wert={sicherheitspasswort}
            pflicht
            hinweis={`Ein eigenes, neues Passwort — mindestens ${MIN_BACKUP_PASSWORT_LAENGE} Zeichen.`}
            onAendern={setSicherheitspasswort}
          />

          <Feld
            name="wh-alter-schluessel"
            beschriftung="Früherer TOKEN_ENCRYPTION_KEY (nur beim Serverumzug)"
            typ="password"
            wert={alterSchluessel}
            hinweis={
              'Nur nötig, wenn das Backup mit einem anderen Schlüssel erzeugt wurde. Die Tokens ' +
              'werden dann mit dem hiesigen Schlüssel neu verschlüsselt.'
            }
            onAendern={setAlterSchluessel}
          />

          <div>
            <Knopf
              onClick={() => {
                void vorbereiten();
              }}
              disabled={!bereit || laeuft}
            >
              {laeuft ? 'Wird geprüft …' : 'Prüfen und Sicherheitsbackup erzeugen'}
            </Knopf>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          <div className="border-rand rounded-md border p-3 text-sm">
            <p>
              <strong>Quelle:</strong> {vorschau.quelle}
            </p>
            <p>
              <strong>Stand vom:</strong> {formatiereZeitpunkt(vorschau.erstelltAm)} (Version{' '}
              {vorschau.anwendungsversion})
            </p>
            <p className="mt-2">
              Eingespielt würden unter anderem {vorschau.zeilen['veranstaltungen'] ?? 0}{' '}
              Veranstaltung(en), {vorschau.zeilen['abstimmungen'] ?? 0} Abstimmung(en) und{' '}
              {vorschau.zeilen['tokens'] ?? 0} Token(s).
            </p>
            {vorschau.tokensWerdenNeuVerschluesselt && (
              <p className="text-warnung mt-2">
                Die Tokens werden mit dem hiesigen <code>TOKEN_ENCRYPTION_KEY</code> neu
                verschlüsselt.
              </p>
            )}
          </div>

          <div className="border-rand rounded-md border p-3 text-sm">
            <p className="font-medium">Schritt 1: Sicherheitsbackup herunterladen</p>
            <p className="text-text-gedaempft mt-1">
              Der aktuelle Stand wurde als <code>{vorschau.sicherheitsbackup}</code> gesichert. Ohne
              den Download geht es nicht weiter — er ist der einzige Weg zurück.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Knopf
                art={geladen ? 'sekundaer' : 'primaer'}
                onClick={() => {
                  void sicherheitsbackupLaden();
                }}
                disabled={laeuft}
              >
                {geladen ? 'Erneut herunterladen' : 'Sicherheitsbackup herunterladen'}
              </Knopf>
              {geladen && <span role="status">✓ heruntergeladen</span>}
            </div>
          </div>

          <div className="border-rand rounded-md border p-3 text-sm">
            <p className="font-medium">Schritt 2: Datenbestand ersetzen</p>
            <p className="text-text-gedaempft mt-1">
              Danach sind alle Sitzungen beendet, und Sie müssen sich neu anmelden.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Knopf
                art="gefahr"
                onClick={() => {
                  void ausfuehren();
                }}
                disabled={!geladen || laeuft}
              >
                {laeuft ? 'Wird eingespielt …' : 'Datenbestand endgültig ersetzen'}
              </Knopf>
              <Knopf
                art="sekundaer"
                onClick={() => {
                  void abbrechen();
                }}
                disabled={laeuft}
              >
                Abbrechen
              </Knopf>
            </div>
          </div>
        </div>
      )}
    </Karte>
  );
}
